import { Component, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
// PrimeNG 21 Imports
import { MessageService, MenuItem, ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { Toast } from 'primeng/toast';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ColumnConfig, TABLE_CONFIG } from '../dashboard/table-config';
import { SupplierDetailModalComponent } from './supplier-detail/supplier-detail.component';
import { SupplierRegistryService } from '../../services/supplier-registry.service';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';
import { ConfirmationDialogService } from '../../../core/services/confirmation-dialog.service';
import { escapeHtml } from '../../../core/validators/no-special-chars.validator';

@Component({
  selector: 'app-suppliers-registry',
  standalone: true,
  providers: [DialogService, MessageService],
  imports: [
    CommonModule,
    DynamicDialogModule,
    Toast,
    RegistryTableComponent,
    BreadcrumbModule,
    ButtonModule,
    ConfirmDialogModule,
  ],
  templateUrl: './suppliers-registry.component.html',
  styleUrl: './suppliers-registry.component.scss',
})
export class SuppliersRegistryComponent implements OnInit {
  private dialogService = inject(DialogService);
  private supplierService = inject(SupplierRegistryService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  private confirmDialogService = inject(ConfirmationDialogService);
  private titleService = inject(Title);

  // Table configuration
  tableTitle = 'Fornitori Registrati';
  tableIcon = 'pi-building';
  columns: ColumnConfig[] = [];
  data = signal<any[]>([]);
  loading = signal<boolean>(false);
  breadcrumbItems = signal<MenuItem[]>([{ label: 'Registro Fornitori' }]);

  ngOnInit(): void {
    this.titleService.setTitle('Registro Fornitori - PalEasy');
    this.loadConfiguration();
    this.loadSuppliers();
  }

  private loadConfiguration(): void {
    const config = TABLE_CONFIG['elencoFornitori'];
    if (config) {
      this.columns = config.columns;
    }
  }

  private loadSuppliers(): void {
    this.loading.set(true);
    this.supplierService.getSuppliers().subscribe({
      next: (response) => {
        if (response?.status && response?.data?.content) {
          // Usa il metodo centralizzato del servizio per mappare i dati
          const mappedData = this.supplierService.mapSuppliersForDisplay(response.data.content);
          this.data.set(mappedData);
        } else {
          console.warn('Unexpected API response format:', response);
          this.messageService.add({
            severity: 'warn',
            summary: 'Attenzione',
            detail: 'Formato dati non valido ricevuto dal server',
            life: 4000,
          });
          this.data.set([]);
        }
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading suppliers:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore nel recupero dei dati',
          detail: "Impossibile caricare l'elenco dei fornitori. Riprova più tardi.",
          life: 5000,
        });
        this.data.set([]);
        this.loading.set(false);
      },
    });
  }

  // =====================
  // EVENT HANDLERS
  // =====================
  onDetailClick(rowData: any): void {
    if (rowData?.id) {
      this.router.navigate(['/axa/supplier', rowData.id]);
    }
  }

  onEditClick(rowData: any): void {
    console.log('onEditClick triggered with rowData:', rowData);
    if (rowData?.id) {
      // Naviga al form wizard con l'ID del fornitore in bozza
      this.router.navigate(['axa/supplier-registration-wizard', rowData.id]);
    }
  }

  onDeleteClick(rowData: any): void {
    console.log('onDeleteClick triggered with rowData:', rowData);
    if (!rowData?.id) return;

    console.log('Calling confirmDialogService.confirmDelete...');
    this.confirmDialogService.confirmDelete(
      `Sei sicuro di voler eliminare il fornitore "${escapeHtml(rowData.supplierName)}"? Questa azione non può essere annullata.`,
      () => {
        console.log('Delete confirmed by user - calling API...');
        // Chiamata API per eliminare il fornitore
        this.supplierService.deleteSupplier(rowData.id).subscribe({
          next: () => {
            console.log('Supplier deleted successfully');
            this.messageService.add({
              severity: 'success',
              summary: 'Fornitore eliminato',
              detail: `Il fornitore "${rowData.supplierName}" è stato eliminato con successo`,
              life: 3000,
            });
            // Ricarica la lista
            this.loadSuppliers();
          },
          error: (error) => {
            console.error('Error deleting supplier:', error);
            this.messageService.add({
              severity: 'error',
              summary: "Errore nell'eliminazione",
              detail: 'Impossibile eliminare il fornitore. Riprova più tardi.',
              life: 5000,
            });
          },
        });
      },
    );
  }

  onExport(): void {
    this.supplierService.exportSuppliersRegistry().subscribe({
      next: (response) => {
        const blob = response.body as Blob | null;
        if (!blob) {
          this.messageService.add({
            severity: 'error',
            summary: 'Errore export',
            detail: 'File non ricevuto dal server',
            life: 4000,
          });
          return;
        }
        let fileName = 'Registro Fornitori.xlsx';
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^";]+)"?/);
          if (match && match[1]) {
            fileName = decodeURIComponent(match[1]);
          }
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Errore export',
          detail: 'Impossibile esportare il registro fornitori',
          life: 4000,
        });
      },
    });
  }

  onClearFilters(): void {
    // Reset data to original
    this.loadSuppliers();
  }

  goToSupplierWizard(): void {
    this.router.navigate(['axa/supplier-registration-wizard']);
  }

  // =====================
  // STATUS HELPERS - Ora centralizzato nel servizio
  // =====================
  // getStatusDisplay() rimosso - usa supplierService.getStatusDisplay()
}
